/**
 * IntegrationsPage - third-party integrations management
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuthStore } from '@stores/authStore';
import { integrationsApi, ragApi, mcpApi, type Integration, type RAGStatus, type MCPToken, type MCPTokenCreated } from '@services/api';
import { saveBlob } from '@/utils/download';
import { useConfirmDialog } from '@/components/ui/ConfirmDialog';
import { PlaudIntegrationPanel } from '@/components/integrations/PlaudIntegrationPanel';

interface IntegrationConfig {
  id: string;
  name: string;
  description: string;
  icon: string;
  category: 'os-bridges' | 'communication' | 'storage' | 'automation' | 'ai' | 'other';
  configFields?: {
    key: string;
    label: string;
    type: 'text' | 'password' | 'url';
    required: boolean;
    placeholder?: string;
  }[];
  // For integrations that require special handling (e.g., desktop app downloads)
  specialType?: 'desktop-app' | 'rag' | 'plaud' | 'resend' | 'mcp';
  downloadUrl?: string;
  setupInstructions?: string[];
}

const availableIntegrations: IntegrationConfig[] = [
  {
    id: 'claude-mcp',
    name: 'Claude Desktop & Claude Code (MCP)',
    description: 'Connect Claude Desktop or Claude Code to NeoKanban via Model Context Protocol',
    icon: '🤖',
    category: 'ai',
    specialType: 'mcp',
    // No downloadUrl: the bundle is fetched from the authenticated backend
    // endpoint (see handleDownloadExtension), which always serves the version
    // built into the running backend image. The old /downloads/ copy came from
    // the frontend image and silently went stale.
  },
  {
    id: 'neo-rag',
    name: 'SPT Neo RAG',
    description: 'AI-powered document search and chat across your projects',
    icon: '🧠',
    category: 'ai',
    specialType: 'rag',
    configFields: [
      { key: 'baseUrl', label: 'RAG API URL', type: 'url', required: true, placeholder: 'https://rag.example.com' },
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
    ],
  },
  {
    id: 'plaud',
    name: 'Plaud AI Recorder',
    description: 'Import recordings from your Plaud AI device and transcribe them',
    icon: '🎙️',
    category: 'ai',
    specialType: 'plaud',
  },
  {
    id: 'resend',
    name: 'Resend',
    description: 'Send transactional emails and share tasks via email',
    icon: '📧',
    category: 'communication',
    specialType: 'resend',
    configFields: [
      { key: 'api_key', label: 'Resend API Key', type: 'password', required: true, placeholder: 're_...' },
      { key: 'from_email', label: 'From Email', type: 'text', required: true, placeholder: 'noreply@yourdomain.com' },
      { key: 'from_name', label: 'From Name', type: 'text', required: false, placeholder: 'NeoKanban' },
    ],
  },
  {
    id: 'macos-bridge',
    name: 'macOS Bridge',
    description: 'Native macOS menu bar app for syncing with Apple Calendar',
    icon: '🍎',
    category: 'os-bridges',
    specialType: 'desktop-app',
    downloadUrl: '/downloads/NeoKanban-Calendar-Bridge.zip',
    setupInstructions: [
      'Download and install the NeoKanban macOS Bridge app',
      'Generate an API token from Settings > API Tokens',
      'Open the app from your menu bar and enter your API token',
      'Select which calendar to sync with and configure sync options',
    ],
  },
  {
    id: 'windows-bridge',
    name: 'Windows Bridge',
    description: 'Native Windows system tray app for syncing with Outlook Calendar',
    icon: '🪟',
    category: 'os-bridges',
    specialType: 'desktop-app',
    downloadUrl: '/downloads/NeoKanban-Windows-Bridge.zip',
    setupInstructions: [
      'Download and install the NeoKanban Windows Bridge app',
      'Generate an API token from Settings > API Tokens',
      'Open the app from your system tray and enter your API token',
      'Select your organization and optionally a default project',
    ],
  },
  {
    id: 'slack',
    name: 'Slack',
    description: 'Get notifications in Slack channels',
    icon: '💬',
    category: 'communication',
    configFields: [
      { key: 'webhookUrl', label: 'Webhook URL', type: 'url', required: true },
      { key: 'channel', label: 'Default Channel', type: 'text', required: false },
    ],
  },
  {
    id: 'discord',
    name: 'Discord',
    description: 'Get notifications in Discord channels',
    icon: '🎮',
    category: 'communication',
    configFields: [
      { key: 'webhookUrl', label: 'Webhook URL', type: 'url', required: true },
    ],
  },
  // Microsoft Teams integration hidden for now
  // {
  //   id: 'teams',
  //   name: 'Microsoft Teams',
  //   description: 'Get notifications in Teams channels',
  //   icon: '👥',
  //   category: 'communication',
  //   configFields: [
  //     { key: 'webhookUrl', label: 'Webhook URL', type: 'url', required: true },
  //   ],
  // },
  {
    id: 'github',
    name: 'GitHub',
    description: 'Link tasks to GitHub issues and PRs',
    icon: '🐙',
    category: 'other',
  },
  {
    id: 'gitlab',
    name: 'GitLab',
    description: 'Link tasks to GitLab issues and MRs',
    icon: '🦊',
    category: 'other',
  },
  {
    id: 'jira',
    name: 'Jira',
    description: 'Import and sync with Jira issues',
    icon: '📋',
    category: 'other',
    configFields: [
      { key: 'domain', label: 'Jira Domain', type: 'url', required: true },
      { key: 'email', label: 'Email', type: 'text', required: true },
      { key: 'apiToken', label: 'API Token', type: 'password', required: true },
    ],
  },
  {
    id: 'google-drive',
    name: 'Google Drive',
    description: 'Attach files from Google Drive',
    icon: '📁',
    category: 'storage',
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    description: 'Attach files from Dropbox',
    icon: '📦',
    category: 'storage',
  },
  {
    id: 'zapier',
    name: 'Zapier',
    description: 'Connect with 5000+ apps via Zapier',
    icon: '⚡',
    category: 'automation',
    configFields: [
      { key: 'apiKey', label: 'API Key', type: 'password', required: true },
    ],
  },
  {
    id: 'webhook',
    name: 'Custom Webhook',
    description: 'Send events to custom webhooks',
    icon: '🔗',
    category: 'automation',
    configFields: [
      { key: 'url', label: 'Webhook URL', type: 'url', required: true },
      { key: 'secret', label: 'Secret Key', type: 'password', required: false },
    ],
  },
];

const categoryLabels: Record<string, string> = {
  ai: 'AI & Intelligence',
  'os-bridges': 'OS Bridges',
  communication: 'Communication',
  storage: 'Cloud Storage',
  automation: 'Automation',
  other: 'Other',
};

export default function IntegrationsPage() {
  const { t } = useTranslation();
  const { currentOrganization } = useAuthStore();

  const [connectedIntegrations, setConnectedIntegrations] = useState<Integration[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Config modal state
  const [configuring, setConfiguring] = useState<IntegrationConfig | null>(null);
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);

  // Desktop app modal state
  const [showingDesktopApp, setShowingDesktopApp] = useState<IntegrationConfig | null>(null);

  // RAG integration state
  const [ragStatus, setRagStatus] = useState<RAGStatus | null>(null);
  const [isLoadingRag, setIsLoadingRag] = useState(false);

  // Resend integration state
  const [isResendConnected, setIsResendConnected] = useState(false);
  const [isLoadingResend, setIsLoadingResend] = useState(false);

  // MCP integration state
  const [mcpTokens, setMcpTokens] = useState<MCPToken[]>([]);
  const [isLoadingMcp, setIsLoadingMcp] = useState(false);
  const [isDownloadingExtension, setIsDownloadingExtension] = useState(false);
  const [showMcpModal, setShowMcpModal] = useState(false);
  const [mcpTokenName, setMcpTokenName] = useState('');
  const [createdMcpToken, setCreatedMcpToken] = useState<MCPTokenCreated | null>(null);
  const [isCreatingMcpToken, setIsCreatingMcpToken] = useState(false);

  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  useEffect(() => {
    if (currentOrganization) {
      loadIntegrations();
      loadRagStatus();
      loadResendStatus();
      loadMcpTokens();
    }
  }, [currentOrganization]);

  const loadResendStatus = async () => {
    setIsLoadingResend(true);
    try {
      const integrations = await integrationsApi.list(currentOrganization?.id || '');
      const resendIntegration = integrations.find((i) => i.type === 'RESEND');
      setIsResendConnected(!!resendIntegration && resendIntegration.status === 'active');
    } catch {
      setIsResendConnected(false);
    } finally {
      setIsLoadingResend(false);
    }
  };

  const loadMcpTokens = async () => {
    if (!currentOrganization) return;
    setIsLoadingMcp(true);
    try {
      const response = await mcpApi.list();
      setMcpTokens(response.items || []);
    } catch {
      setMcpTokens([]);
    } finally {
      setIsLoadingMcp(false);
    }
  };

  /**
   * Fetch the .mcpb bundle through the API client and save it.
   *
   * The endpoint is authenticated: a plain download link sends no bearer token
   * and the browser reports "file not available".
   */
  const handleDownloadExtension = async () => {
    setIsDownloadingExtension(true);
    try {
      const bundle = await mcpApi.downloadExtension();
      saveBlob(bundle, 'neokanban.mcpb');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to download the extension bundle'
      );
    } finally {
      setIsDownloadingExtension(false);
    }
  };

  const handleCreateMcpToken = async () => {
    if (!currentOrganization || !mcpTokenName.trim()) return;
    setIsCreatingMcpToken(true);
    try {
      const token = await mcpApi.create({
        name: mcpTokenName,
        organizationId: currentOrganization.id,
      });
      setCreatedMcpToken(token);
      setMcpTokenName('');
      await loadMcpTokens();
    } catch (err) {
      console.error('Failed to create MCP token:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to create MCP token');
    } finally {
      setIsCreatingMcpToken(false);
    }
  };

  const handleDeleteMcpToken = async (tokenId: string, tokenName: string) => {
    const confirmed = await confirm({
      title: 'Delete MCP Token',
      description: `Are you sure you want to delete "${tokenName}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await mcpApi.revoke(tokenId);
      await loadMcpTokens();
    } catch (err) {
      console.error('Failed to delete MCP token:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete MCP token');
    }
  };

  const getMcpConfigSnippet = (token: MCPTokenCreated) => {
    const apiUrl = window.location.origin;
    const mcpEndpoint = `${apiUrl}/api/v1/mcp/messages`;

    return `{
  "mcpServers": {
    "neokanban": {
      "command": "npx",
      "args": ["-y", "@modelcontextprotocol/server-http"],
      "env": {
        "HTTP_URL": "${mcpEndpoint}",
        "HTTP_HEADERS": "{\\"Authorization\\": \\"Bearer ${token.token}\\"}"
      }
    }
  }
}`;
  };

  const loadRagStatus = async () => {
    setIsLoadingRag(true);
    try {
      const status = await ragApi.getStatus();
      setRagStatus(status);
    } catch {
      // RAG not configured, status will be null
      setRagStatus(null);
    } finally {
      setIsLoadingRag(false);
    }
  };

  const loadIntegrations = async () => {
    if (!currentOrganization) return;

    setIsLoading(true);
    setError(null);

    try {
      const integrations = await integrationsApi.list(currentOrganization.id);
      setConnectedIntegrations(integrations);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load integrations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnect = async (integration: IntegrationConfig) => {
    if (!currentOrganization) return;

    // Clear any previous connect error
    setConnectError(null);

    // If it's a desktop app, show the setup instructions modal
    if (integration.specialType === 'desktop-app') {
      setShowingDesktopApp(integration);
      return;
    }

    // If it's RAG or other integration with config fields, show modal
    if (integration.configFields && integration.configFields.length > 0) {
      setConfiguring(integration);
      setConfigValues({});
      return;
    }

    // Otherwise, initiate OAuth flow
    try {
      const authUrl = await integrationsApi.getAuthUrl(
        currentOrganization.id,
        integration.id
      );
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get auth URL:', err);
      // Extract error message from API response or use generic message
      const errorMessage = err instanceof Error ? err.message : 'Failed to connect';
      // Check if it's an Axios error with response data
      const axiosError = err as { response?: { data?: { detail?: string } } };
      const detail = axiosError.response?.data?.detail;
      setConnectError(detail || errorMessage);
    }
  };

  const handleSaveConfig = async () => {
    if (!currentOrganization || !configuring) return;

    setIsSaving(true);
    setConfigError(null);

    try {
      // Handle RAG integration separately via its own API
      if (configuring.specialType === 'rag') {
        await ragApi.configure({
          baseUrl: configValues.baseUrl ?? '',
          apiKey: configValues.apiKey ?? '',
        });
        await loadRagStatus();
      } else if (configuring.specialType === 'resend') {
        await handleConnectResend();
        return; // handleConnectResend manages state
      } else {
        await integrationsApi.connect(currentOrganization.id, {
          type: configuring.id,
          config: configValues,
        });
        await loadIntegrations();
      }
      setConfiguring(null);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to connect integration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnect = async (integrationId: string, integrationName: string) => {
    if (!currentOrganization) return;

    const confirmed = await confirm({
      title: t('settings.disconnectIntegration'),
      description: t('settings.confirmDisconnect') + ` (${integrationName})`,
      confirmLabel: t('common.disconnect'),
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await integrationsApi.disconnect(currentOrganization.id, integrationId);
      setConnectedIntegrations((prev) => prev.filter((i) => i.id !== integrationId));
    } catch (err) {
      console.error('Failed to disconnect integration:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect integration');
    }
  };

  const handleDisconnectRag = async () => {
    const confirmed = await confirm({
      title: t('settings.disconnectIntegration'),
      description: t('settings.confirmDisconnect') + ' (SPT Neo RAG)',
      confirmLabel: t('common.disconnect'),
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await ragApi.disconnect();
      setRagStatus(null);
    } catch (err) {
      console.error('Failed to disconnect RAG:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect SPT Neo RAG');
    }
  };

  const handleConnectResend = async () => {
    if (!currentOrganization || !configuring) return;

    setIsSaving(true);
    setConfigError(null);

    try {
      await integrationsApi.connectResend({
        api_key: configValues.api_key ?? '',
        from_email: configValues.from_email ?? '',
        from_name: configValues.from_name ?? 'NeoKanban',
      });
      await loadResendStatus();
      setConfiguring(null);
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to connect Resend');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisconnectResend = async () => {
    const confirmed = await confirm({
      title: t('settings.disconnectIntegration'),
      description: t('settings.confirmDisconnect') + ' (Resend)',
      confirmLabel: t('common.disconnect'),
      variant: 'warning',
    });

    if (!confirmed) return;

    try {
      await integrationsApi.disconnectResend();
      setIsResendConnected(false);
    } catch (err) {
      console.error('Failed to disconnect Resend:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to disconnect Resend');
    }
  };

  const isConnected = (integrationId: string) => {
    return connectedIntegrations.some((i) => i.type === integrationId);
  };

  const getConnectedIntegration = (integrationId: string) => {
    return connectedIntegrations.find((i) => i.type === integrationId);
  };

  const groupedIntegrations = availableIntegrations.reduce(
    (acc, integration) => {
      const category = integration.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(integration);
      return acc;
    },
    {} as Record<string, IntegrationConfig[]>
  );

  if (!currentOrganization) {
    return (
      <div className="p-6">
        <p className="text-neutral-500">{t('settings.noOrganization')}</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h2 className="text-lg font-medium text-neutral-900 dark:text-white">
          {t('settings.integrations')}
        </h2>
        <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
          {t('settings.integrationsDescription')}
        </p>
      </div>

      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
          <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {connectError && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg flex items-start justify-between">
          <p className="text-sm text-amber-700 dark:text-amber-400">{connectError}</p>
          <button
            onClick={() => setConnectError(null)}
            className="text-amber-500 hover:text-amber-700 dark:hover:text-amber-300"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(groupedIntegrations).map(([category, integrations]) => (
            <div key={category}>
              <h3 className="text-sm font-medium text-neutral-500 uppercase tracking-wider mb-4">
                {categoryLabels[category]}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {integrations.map((integration) => {
                  // Special handling for MCP integration
                  if (integration.specialType === 'mcp') {
                    const isMcpConnected = mcpTokens.length > 0;
                    return (
                      <div
                        key={integration.id}
                        className={`p-5 border rounded-xl transition-colors ${
                          isMcpConnected
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                            : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xl dark:bg-neutral-800">{integration.icon}</span>
                            <div>
                              <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                                {integration.name}
                              </h4>
                              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                                {integration.description}
                              </p>
                            </div>
                          </div>
                          {isLoadingMcp ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                          ) : (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleDownloadExtension}
                                disabled={isDownloadingExtension}
                                className="px-2 py-1 text-xs font-medium text-primary-600 border border-primary-300 rounded-md transition-colors hover:bg-primary-50 disabled:opacity-60 disabled:cursor-not-allowed dark:border-primary-700 dark:hover:bg-primary-900/20"
                              >
                                {isDownloadingExtension ? '⏳ Downloading…' : '📥 Download .mcpb'}
                              </button>
                              <button
                                onClick={() => setShowMcpModal(true)}
                                className="px-3 py-1 text-xs font-medium text-primary-600 border border-primary-300 rounded-md transition-colors hover:bg-primary-50 dark:border-primary-700 dark:hover:bg-primary-900/20"
                              >
                                {isMcpConnected ? 'Manage Tokens' : 'Create Token'}
                              </button>
                            </div>
                          )}
                        </div>
                        {/* Show MCP tokens when connected */}
                        {isMcpConnected && (
                          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800 space-y-2">
                            <p className="text-xs font-medium text-neutral-600 dark:text-neutral-400">
                              Active Tokens ({mcpTokens.length})
                            </p>
                            {mcpTokens.slice(0, 3).map((token) => (
                              <div key={token.id} className="flex items-center justify-between text-xs">
                                <div className="flex items-center gap-2">
                                  <span className="truncate max-w-[180px]">{token.name}</span>
                                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                                    !token.isExpired
                                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                  }`}>
                                    {!token.isExpired ? 'Active' : 'Inactive'}
                                  </span>
                                </div>
                                <button
                                  onClick={() => handleDeleteMcpToken(token.id, token.name)}
                                  className="text-red-600 hover:text-red-700 dark:text-red-400"
                                >
                                  Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Special handling for RAG integration
                  if (integration.specialType === 'rag') {
                    const isRagConnected = ragStatus?.isConfigured ?? false;
                    return (
                      <div
                        key={integration.id}
                        className={`p-5 border rounded-xl transition-colors ${
                          isRagConnected
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                            : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xl dark:bg-neutral-800">{integration.icon}</span>
                            <div>
                              <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                                {integration.name}
                              </h4>
                              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                                {integration.description}
                              </p>
                            </div>
                          </div>
                          {isLoadingRag ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                          ) : isRagConnected ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-green-600 dark:text-green-400">
                                Connected
                              </span>
                              <button
                                onClick={handleDisconnectRag}
                                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                              >
                                Disconnect
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleConnect(integration)}
                              className="px-3 py-1 text-xs font-medium text-primary-600 border border-primary-300 rounded-md transition-colors hover:bg-primary-50 dark:border-primary-700 dark:hover:bg-primary-900/20"
                            >
                              Connect
                            </button>
                          )}
                        </div>
                        {/* Show RAG stats when connected */}
                        {isRagConnected && ragStatus && (
                          <div className="mt-3 pt-3 border-t border-green-200 dark:border-green-800 flex items-center gap-4 text-xs text-neutral-600 dark:text-neutral-400">
                            <span>{ragStatus.totalKnowledgeBases} knowledge bases</span>
                            <span>{ragStatus.totalDocuments} documents</span>
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Special handling for Resend integration
                  if (integration.specialType === 'resend') {
                    return (
                      <div
                        key={integration.id}
                        className={`p-5 border rounded-xl transition-colors ${
                          isResendConnected
                            ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                            : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xl dark:bg-neutral-800">{integration.icon}</span>
                            <div>
                              <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                                {integration.name}
                              </h4>
                              <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                                {integration.description}
                              </p>
                            </div>
                          </div>
                          {isLoadingResend ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                          ) : isResendConnected ? (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-green-600 dark:text-green-400">
                                Connected
                              </span>
                              <button
                                onClick={handleDisconnectResend}
                                className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                              >
                                Disconnect
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleConnect(integration)}
                              className="px-3 py-1 text-xs font-medium text-primary-600 border border-primary-300 rounded-md transition-colors hover:bg-primary-50 dark:border-primary-700 dark:hover:bg-primary-900/20"
                            >
                              Connect
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  }

                  // Special handling for Plaud integration
                  if (integration.specialType === 'plaud') {
                    return (
                      <div
                        key={integration.id}
                        className="p-5 border rounded-xl transition-colors border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xl dark:bg-neutral-800">{integration.icon}</span>
                          <div>
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                              {integration.name}
                            </h4>
                            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                              {integration.description}
                            </p>
                          </div>
                        </div>
                        <PlaudIntegrationPanel />
                      </div>
                    );
                  }

                  const connected = isConnected(integration.id);
                  const connectedData = getConnectedIntegration(integration.id);

                  return (
                    <div
                      key={integration.id}
                      className={`p-5 border rounded-xl transition-colors ${
                        connected
                          ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
                          : 'border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-neutral-100 text-xl dark:bg-neutral-800">{integration.icon}</span>
                          <div>
                            <h4 className="text-sm font-medium text-neutral-900 dark:text-white">
                              {integration.name}
                            </h4>
                            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                              {integration.description}
                            </p>
                          </div>
                        </div>
                        {connected ? (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-green-600 dark:text-green-400">
                              Connected
                            </span>
                            <button
                              onClick={() => handleDisconnect(connectedData!.id, integration.name)}
                              className="text-xs text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                              Disconnect
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleConnect(integration)}
                            className="px-3 py-1 text-xs font-medium text-primary-600 border border-primary-300 rounded-md transition-colors hover:bg-primary-50 dark:border-primary-700 dark:hover:bg-primary-900/20"
                          >
                            Connect
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Config Modal */}
      {configuring && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{configuring.icon}</span>
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                {t('settings.configure')} {configuring.name}
              </h3>
            </div>

            {configError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <p className="text-sm text-red-600 dark:text-red-400">{configError}</p>
              </div>
            )}

            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveConfig();
              }}
              className="space-y-4"
            >
              {configuring.configFields?.map((field) => (
                <div key={field.key}>
                  <label
                    htmlFor={field.key}
                    className="block text-sm font-medium text-neutral-700 dark:text-neutral-300"
                  >
                    {field.label}
                    {field.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type={field.type}
                    id={field.key}
                    value={configValues[field.key] ?? ''}
                    onChange={(e) =>
                      setConfigValues((prev) => ({
                        ...prev,
                        [field.key]: e.target.value,
                      }))
                    }
                    className="mt-1 block w-full rounded-md border border-neutral-300 bg-white text-neutral-900 shadow-sm focus:border-primary-500 focus:ring-primary-500 dark:border-neutral-600 dark:bg-neutral-700 dark:text-white sm:text-sm"
                    required={field.required}
                  />
                </div>
              ))}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setConfiguring(null);
                    setConfigValues({});
                    setConfigError(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={isSaving}
                  className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {isSaving ? t('common.saving') : t('common.connect')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Desktop App Modal */}
      {showingDesktopApp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">{showingDesktopApp.icon}</span>
              <div>
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                  {showingDesktopApp.name}
                </h3>
                <p className="text-sm text-neutral-500">{showingDesktopApp.description}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-neutral-50 dark:bg-neutral-700/50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-neutral-900 dark:text-white mb-3">
                  {t('settings.setupInstructions')}
                </h4>
                <ol className="space-y-2">
                  {showingDesktopApp.setupInstructions?.map((instruction, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full flex items-center justify-center text-sm font-medium">
                        {index + 1}
                      </span>
                      <span className="text-sm text-neutral-700 dark:text-neutral-300 pt-0.5">
                        {instruction}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>{t('settings.tip')}:</strong> {t('settings.apiTokenRequired')}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowingDesktopApp(null)}
                  className="px-4 py-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded-md"
                >
                  {t('common.close')}
                </button>
                {showingDesktopApp.downloadUrl && (
                  <a
                    href={showingDesktopApp.downloadUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-md hover:bg-primary-700 inline-flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    {t('settings.downloadApp')}
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MCP Token Modal */}
      {showMcpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-neutral-900 dark:text-white">
                🤖 MCP Tokens for Claude Desktop & Claude Code
              </h3>
              <button
                onClick={() => {
                  setShowMcpModal(false);
                  setCreatedMcpToken(null);
                }}
                className="text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {createdMcpToken ? (
              // Show the newly created token (only once!)
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-2">
                    ✅ Token created successfully!
                  </p>
                  <p className="text-xs text-green-700 dark:text-green-400">
                    Save this token now - it won't be shown again.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Token (copy this now!)
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={createdMcpToken.token}
                      readOnly
                      className="flex-1 px-3 py-2 bg-neutral-50 dark:bg-neutral-700 border border-neutral-300 dark:border-neutral-600 rounded-md text-sm font-mono"
                    />
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(createdMcpToken.token);
                      }}
                      className="px-3 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Config for Claude Desktop (JSON method)
                  </label>
                  <pre className="p-3 bg-neutral-900 text-green-400 rounded-md text-xs overflow-x-auto">
                    {getMcpConfigSnippet(createdMcpToken)}
                  </pre>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                    📦 Easier Method: Use .mcpb Extension
                  </p>
                  <ol className="text-xs text-blue-700 dark:text-blue-400 space-y-1">
                    <li>1. Download the .mcpb bundle (button in main integration card)</li>
                    <li>2. Drag it into Claude Desktop → Settings → Extensions</li>
                    <li>3. Configure with your token above</li>
                  </ol>
                </div>

                <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-lg p-4">
                  <p className="text-sm font-medium text-violet-800 dark:text-violet-300 mb-2">
                    💻 Claude Code: Install the Plugin
                  </p>
                  <pre className="p-2 mb-2 bg-neutral-900 text-violet-300 rounded-md text-xs overflow-x-auto">
{`/plugin marketplace add ezeeFlop/claude-plugins
/plugin install neokanban@sponge-theory`}
                  </pre>
                  <p className="text-xs text-violet-700 dark:text-violet-400">
                    Claude Code opens an interactive form during install — paste the token above
                    when asked. Missed it? Run{' '}
                    <code className="font-mono">/plugin configure neokanban@sponge-theory</code>.
                  </p>
                </div>

                <button
                  onClick={() => setCreatedMcpToken(null)}
                  className="w-full px-4 py-2 bg-neutral-600 text-white rounded-md hover:bg-neutral-700"
                >
                  Done
                </button>
              </div>
            ) : (
              // Show token creation form and existing tokens
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Create New Token
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={mcpTokenName}
                      onChange={(e) => setMcpTokenName(e.target.value)}
                      placeholder="Token name (e.g., 'My MacBook')"
                      className="flex-1 px-3 py-2 border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white rounded-md text-sm"
                    />
                    <button
                      onClick={handleCreateMcpToken}
                      disabled={!mcpTokenName.trim() || isCreatingMcpToken}
                      className="px-4 py-2 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700 disabled:opacity-50"
                    >
                      {isCreatingMcpToken ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </div>

                {mcpTokens.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Existing Tokens
                    </h4>
                    <div className="space-y-2">
                      {mcpTokens.map((token) => (
                        <div
                          key={token.id}
                          className="flex items-center justify-between p-3 border border-neutral-200 hover:border-neutral-300 dark:border-neutral-700 dark:hover:border-neutral-600 rounded-md"
                        >
                          <div>
                            <p className="text-sm font-medium text-neutral-900 dark:text-white">
                              {token.name}
                            </p>
                            <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                              Created: {new Date(token.createdAt).toLocaleDateString()}
                              {token.lastUsedAt && ` • Last used: ${new Date(token.lastUsedAt).toLocaleDateString()}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 text-xs rounded ${
                              !token.isExpired
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {!token.isExpired ? 'Active' : 'Inactive'}
                            </span>
                            <button
                              onClick={() => handleDeleteMcpToken(token.id, token.name)}
                              className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    <strong>Note:</strong> Claude Desktop: download the .mcpb extension and drag it
                    into Settings → Extensions. Claude Code: install the plugin with{' '}
                    <code className="font-mono">/plugin install neokanban@sponge-theory</code> (after{' '}
                    <code className="font-mono">/plugin marketplace add ezeeFlop/claude-plugins</code>).
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {ConfirmDialogComponent}
    </div>
  );
}
